import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Trash2, Download, Mail, Phone, MapPin, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSignedUp = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Australia/Melbourne",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function AdminSubscribers({ token, onFlash }) {
  const headers = { "X-Admin-Token": token };
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subscribers`, { headers });
      setSubscribers(r.data || []);
    } catch (e) {
      console.error(e);
      onFlash?.("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the mailing list?`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers });
      onFlash?.("Subscriber removed.");
      await load();
    } catch (e) {
      console.error(e);
      onFlash?.("Delete failed.");
    }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/subscribers/export`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "thebar-mailing-list.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onFlash?.("CSV downloaded.");
    } catch (e) {
      console.error(e);
      onFlash?.("CSV download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const filtered = subscribers.filter((s) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.mobile || "").includes(q) ||
      (s.postcode || "").includes(q)
    );
  });

  return (
    <div data-testid="admin-subscribers-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#F5A623] font-semibold">
            / Mailing List
          </span>
          <h1 className="font-display text-4xl uppercase mt-2">Your subscribers</h1>
          <p className="text-sm text-[#FDFBF7]/55 mt-2 max-w-xl">
            Anyone who's signed up at{" "}
            <span className="text-[#F5A623]">/join</span>. Export the list as
            CSV to import into Mailchimp, MailerLite, or an SMS tool.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5">
            <Users size={16} className="text-[#F5A623]" />
            <span
              data-testid="subscribers-count"
              className="font-display text-xl leading-none"
            >
              {subscribers.length}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/60">
              total
            </span>
          </div>
          <button
            onClick={downloadCsv}
            disabled={downloading || subscribers.length === 0}
            data-testid="subscribers-export-csv"
            className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold uppercase tracking-wider text-xs px-5 py-3"
          >
            {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Export CSV
          </button>
        </div>
      </div>

      <div className="mb-5">
        <input
          type="search"
          placeholder="Search by name, email, mobile or postcode…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="subscribers-search"
          className="w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-4 py-3 text-[#FDFBF7] placeholder:text-[#FDFBF7]/25 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-[#FDFBF7]/60 py-10">
          <Loader2 className="animate-spin" /> Loading subscribers…
        </div>
      ) : subscribers.length === 0 ? (
        <div className="border border-white/10 bg-[#111114] py-16 px-6 text-center text-[#FDFBF7]/60" data-testid="subscribers-empty">
          <Mail size={32} className="mx-auto mb-3 text-[#F5A623]/70" />
          <p className="font-display text-xl uppercase text-[#FDFBF7] mb-1">
            No subscribers yet
          </p>
          <p className="text-sm">
            Share <span className="text-[#F5A623]">/join</span> on your Facebook
            page or via a QR code at the venue.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 bg-[#111114] py-10 text-center text-[#FDFBF7]/60 text-sm">
          No matches for "{query}".
        </div>
      ) : (
        <ul className="border border-white/10 divide-y divide-white/10">
          {filtered.map((s) => (
            <li
              key={s.id}
              data-testid={`subscriber-row-${s.id}`}
              className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 bg-[#111114]"
            >
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg uppercase truncate">
                  {s.first_name} {s.last_name}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#FDFBF7]/70">
                  <span className="inline-flex items-center gap-1.5">
                    <Mail size={12} className="text-[#F5A623]" />
                    <a
                      href={`mailto:${s.email}`}
                      className="hover:text-[#F5A623] transition-colors truncate max-w-[220px]"
                    >
                      {s.email}
                    </a>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone size={12} className="text-[#F5A623]" />
                    <a
                      href={`tel:${s.mobile}`}
                      className="hover:text-[#F5A623] transition-colors"
                    >
                      {s.mobile}
                    </a>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={12} className="text-[#F5A623]" />
                    {s.postcode}
                  </span>
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-[#FDFBF7]/40">
                  Signed up · {fmtSignedUp(s.created_at)}
                </div>
              </div>
              <button
                onClick={() => remove(s.id, `${s.first_name} ${s.last_name}`)}
                data-testid={`subscriber-delete-${s.id}`}
                aria-label={`Delete ${s.first_name} ${s.last_name}`}
                className="self-end sm:self-auto p-2 text-[#FDFBF7]/60 hover:text-red-400 transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
